import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export function Prefooter() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email.trim()) {
      setSubmitted(true)
    }
  }

  return (
    <section
      id="newsletter"
      className="relative bg-argan text-bruno overflow-hidden"
      aria-label="Iscriviti alla newsletter"
    >
      {/* Decorative oversized italic */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-12 -right-8 md:-right-20 select-none font-display italic text-cream/12 text-[18rem] md:text-[28rem] leading-none"
        style={{ color: 'rgba(255, 250, 243, 0.12)' }}
      >
        N
      </div>

      <div className="nashi-grain opacity-50" />

      <div className="relative z-10 nashi-container py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — Pitch */}
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-bruno"
            >
              <span className="block w-8 h-px bg-bruno" />
              Newsletter
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1.1, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2.5rem, 5.5vw, 4.75rem)',
                lineHeight: 1,
                letterSpacing: '-0.02em',
              }}
            >
              <span className="block">Iscriviti alla</span>
              <span className="block italic font-light">newsletter Nashi.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="mt-8 max-w-md text-bruno/80 text-base md:text-lg leading-relaxed"
            >
              Resta aggiornata su novità, edizioni speciali e gli appuntamenti
              del mondo Nashi Argan.
            </motion.p>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-5 lg:pt-12">
            {!submitted ? (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 1, delay: 0.5 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label
                    htmlFor="newsletter-email"
                    className="block text-[10px] uppercase tracking-[0.32em] text-bruno/70 mb-3"
                  >
                    La tua email
                  </label>
                  <div className="relative">
                    <input
                      id="newsletter-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@email.it"
                      className="w-full bg-transparent border-b border-bruno/40 py-3 pr-12 text-bruno placeholder:text-bruno/40 placeholder:italic focus:outline-none focus:border-bruno transition-colors text-lg"
                    />
                    <button
                      type="submit"
                      aria-label="Iscriviti"
                      className="absolute right-0 bottom-3 text-bruno hover:text-cream transition-colors group"
                    >
                      <ArrowRight
                        className="h-5 w-5 transition-transform duration-500 group-hover:translate-x-1"
                        strokeWidth={1.5}
                      />
                    </button>
                  </div>
                </div>

                <label className="flex items-start gap-3 text-[11px] tracking-wide text-bruno/75 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    className="mt-1 accent-bruno w-3.5 h-3.5"
                  />
                  <span>
                    Dichiaro di aver letto e capito la{' '}
                    <LocalizedLink to="privacy" className="underline underline-offset-2 hover:text-bruno transition-colors">
                      Privacy policy
                    </LocalizedLink>
                    . Do il consenso all'utilizzo dei miei dati personali per ricevere newsletter e comunicazioni da Nashi Argan.
                  </span>
                </label>

                <button
                  type="submit"
                  className="group inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno hover:gap-4 transition-all"
                >
                  Iscriviti
                  <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                    →
                  </span>
                </button>
              </motion.form>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-4"
              >
                <span className="font-display italic text-bruno text-3xl md:text-4xl">
                  Grazie.
                </span>
                <p className="text-bruno/80 text-base leading-relaxed">
                  Ti abbiamo inviato una mail di conferma. Controlla la tua
                  casella per completare l'iscrizione alla newsletter Nashi Argan.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
